// MUI theme for the Menus workspace ONLY. AdvancedMenus passes this function to
// a nested ThemeProvider, so it extends the app theme for everything rendered
// inside the workspace — including its dialogs, menus and popovers, which are
// portaled in the DOM but still sit inside the React tree. Pure: no side
// effects on import, and nothing outside the workspace sees it.
import { createTheme } from '@mui/material/styles'
import { mw } from './menuWorkspaceStyles'

const C = mw.color

const TYPE_VARIANTS = [
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2',
  'body1', 'body2', 'button', 'caption', 'overline'
]

// The app theme's variants each carry their own fontFamily, so swapping the
// top-level fontFamily alone would not reach them.
const typographyFor = (outer) => {
  const typo = { ...outer.typography, fontFamily: mw.font.body }
  TYPE_VARIANTS.forEach((k) => {
    typo[k] = { ...(outer.typography[k] || {}), fontFamily: mw.font.body }
  })
  typo.body1 = { ...typo.body1, fontSize: '0.9375rem' }
  typo.body2 = { ...typo.body2, fontSize: '0.875rem' }
  typo.button = { ...typo.button, textTransform: 'none', fontWeight: 600, letterSpacing: 0 }
  typo.caption = { ...typo.caption, letterSpacing: 0 }
  return typo
}

const components = {
  MuiTypography: {
    styleOverrides: {
      root: { color: C.textPrimary },
      colorTextSecondary: { color: C.textMuted },
    },
  },
  MuiPaper: {
    styleOverrides: { root: { color: C.textPrimary } },
  },

  // ---- Buttons ----
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: mw.radius.sm,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none' },
      },
      sizeMedium: { minHeight: 44, paddingLeft: 18, paddingRight: 18, fontSize: 14.5 },
      sizeSmall: { minHeight: 32, borderRadius: mw.radius.xs, paddingLeft: 12, paddingRight: 12, fontSize: 13.5 },
      containedPrimary: {
        backgroundColor: C.accent,
        boxShadow: mw.shadow.cta,
        '&:hover': { backgroundColor: C.accentDark, boxShadow: mw.shadow.cta },
      },
      // Default-colour outlined buttons only; error/warning keep their colour.
      outlinedPrimary: {
        backgroundColor: C.cardBg,
        borderColor: C.border,
        color: C.textPrimary,
        '&:hover': { backgroundColor: C.headBg, borderColor: C.borderStrong },
      },
      textPrimary: {
        color: C.accent,
        '&:hover': { backgroundColor: C.accentSoft },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: mw.radius.xs,
        '&:hover': { backgroundColor: C.subtleBg },
      },
    },
  },

  // ---- Inputs ----
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: mw.radius.sm,
        backgroundColor: C.cardBg,
        transition: 'box-shadow .15s ease',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.borderStrong },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.accent, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${C.accentSoft}` },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: C.danger },
      },
      // Medium fields: 46px tall instead of MUI's 56px.
      input: { color: C.textPrimary, padding: '12px 14px' },
      inputSizeSmall: { padding: '9px 12px' },
      multiline: { padding: '12px 14px' },
      inputMultiline: { padding: 0 },
      inputAdornedStart: { paddingLeft: 0 },
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: { color: C.textPrimary, '&::placeholder': { color: C.textFaint, opacity: 1 } },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: C.textMuted,
        '&.Mui-focused': { color: C.accent },
      },
      // Re-centre the resting label for the shorter medium field.
      outlined: {
        '&:not(.MuiInputLabel-sizeSmall):not(.MuiInputLabel-shrink)': {
          transform: 'translate(14px, 12px) scale(1)',
        },
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: { root: { color: C.textMuted, marginLeft: 2, lineHeight: 1.45 } },
  },
  MuiSelect: {
    styleOverrides: {
      select: { color: C.textPrimary },
      icon: { color: C.textMuted },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: { label: { color: C.textSecondary, fontSize: 14.5 } },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        color: C.borderStrong,
        '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: C.accent },
      },
    },
  },

  // ---- Switch: green track when on, white knob ----
  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 44,
        height: 26,
        padding: 0,
        margin: 6,
        overflow: 'visible',
        '& .MuiSwitch-switchBase': {
          padding: 3,
          color: '#fff',
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#fff',
            '& + .MuiSwitch-track': { backgroundColor: C.on, opacity: 1 },
          },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.4 },
          '&:hover': { backgroundColor: 'transparent' },
        },
        '& .MuiSwitch-thumb': {
          width: 20,
          height: 20,
          boxShadow: '0 1px 3px rgba(23,24,30,0.25)',
        },
        '& .MuiSwitch-track': {
          borderRadius: 13,
          backgroundColor: C.countIdle,
          opacity: 1,
          transition: 'background-color .18s ease',
        },
      },
      sizeSmall: {
        width: 38,
        height: 22,
        '& .MuiSwitch-switchBase': {
          padding: 3,
          '&.Mui-checked': { transform: 'translateX(16px)' },
        },
        '& .MuiSwitch-thumb': { width: 16, height: 16 },
        '& .MuiSwitch-track': { borderRadius: 11 },
      },
    },
  },

  // ---- Chips ----
  MuiChip: {
    styleOverrides: {
      root: { color: C.textSecondary, fontWeight: 600, borderRadius: mw.radius.pill },
      outlined: { color: C.textSecondary, borderColor: C.border },
      colorPrimary: { color: '#fff' },
      colorError: { color: '#fff' },
      colorSuccess: { color: C.textPrimary },
      colorWarning: { color: C.textPrimary },
    },
  },

  // ---- Tables ----
  MuiTableCell: {
    styleOverrides: {
      root: {
        color: C.textPrimary,
        fontSize: 14.5,
        padding: '14px 24px',
        borderBottom: `1px solid ${C.divider}`,
      },
      head: { color: C.textMuted, fontWeight: 700 },
      paddingCheckbox: { padding: '0 0 0 14px', width: 56 },
      sizeSmall: { padding: '8px 12px', fontSize: 13.5 },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&.MuiTableRow-hover:hover': { backgroundColor: C.hoverBg },
        transition: 'background-color .12s ease',
      },
    },
  },

  // ---- Tabs (fallback colours; tab bars set their own sx) ----
  MuiTab: {
    styleOverrides: {
      root: {
        color: C.textMuted,
        '&.Mui-selected': { color: C.textPrimary },
      },
    },
  },

  // ---- Dialogs: white, radius 24, over a dark scrim ----
  MuiDialog: {
    styleOverrides: {
      root: { '& .MuiBackdrop-root': { backgroundColor: C.scrim } },
      paper: {
        color: C.textPrimary,
        backgroundColor: C.cardBg,
        borderRadius: mw.radius.lg,
        boxShadow: '0 30px 80px -20px rgba(15,15,20,0.45)',
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        color: C.textPrimary,
        fontFamily: mw.font.display,
        fontWeight: 700,
        fontSize: 26,
        letterSpacing: '-0.015em',
        lineHeight: 1.15,
        padding: '24px 28px 8px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: { root: { padding: '8px 28px 20px' } },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '14px 28px 22px',
        gap: 8,
        '& > :not(:first-of-type)': { marginLeft: 0 },
      },
    },
  },

  // ---- Menus / popovers: shadowed, rounded ----
  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: mw.radius.sm,
        border: `1px solid ${C.border}`,
        boxShadow: mw.shadow.pop,
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        color: C.textPrimary,
        fontSize: 14,
        borderRadius: 8,
        marginLeft: 6,
        marginRight: 6,
        '&.Mui-selected': { backgroundColor: C.accentSoft },
        '&.Mui-selected:hover': { backgroundColor: '#FBE3D6' },
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: { color: C.textPrimary },
      secondary: { color: C.textMuted },
    },
  },
  MuiDivider: {
    styleOverrides: { root: { borderColor: C.divider } },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        color: '#fff',
        backgroundColor: C.textPrimary,
        fontSize: 12,
        fontWeight: 500,
        borderRadius: 8,
        padding: '6px 10px',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: { borderRadius: mw.radius.sm },
      message: { color: 'inherit' },
    },
  },
}

export const menuWorkspaceTheme = (outer) => createTheme({
  ...outer,
  palette: {
    ...outer.palette,
    primary: { main: C.accent, dark: C.accentDark, light: C.accentSoft, contrastText: '#fff' },
    success: { ...outer.palette.success, main: C.on, contrastText: '#fff' },
    text: { ...outer.palette.text, primary: C.textPrimary, secondary: C.textMuted },
    divider: C.divider,
    background: { ...outer.palette.background, paper: C.cardBg },
  },
  typography: typographyFor(outer),
  components: { ...outer.components, ...components },
})
